import { useState } from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/hooks/useLanguage";

interface FAQItem {
  question: string;
  answer: string;
}

interface FAQCategory {
  id: string;
  title: string;
  items: FAQItem[];
}

const FAQContent = () => {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const { t, tObject } = useLanguage();

  const faqData = tObject<FAQCategory[]>("faqContent.categories");

  const displayedCategories = selectedCategory
    ? faqData.filter((cat) => cat.id === selectedCategory)
    : faqData;

  const totalFAQs = faqData.reduce((sum, cat) => sum + cat.items.length, 0);

  return (
    <section className="py-16 bg-background">
      <div className="container mx-auto px-4 md:px-6">
        <div className="flex flex-col lg:flex-row gap-12">
          {/* Sidebar - Category Filter */}
          <aside className="lg:w-64 shrink-0">
            <div className="lg:sticky lg:top-24">
              <h3 className="font-semibold text-foreground mb-4">{t("faqContent.categoriesLabel")}</h3>
              <nav className="space-y-1">
                <button
                  onClick={() => setSelectedCategory(null)}
                  className={cn(
                    "block w-full text-left px-3 py-2 rounded-lg transition-colors",
                    selectedCategory === null
                      ? "bg-chathero-mint/10 text-chathero-mint font-medium border-l-2 border-chathero-mint"
                      : "text-muted-foreground hover:text-foreground hover:bg-secondary/50"
                  )}
                >
                  {t("faqContent.allQuestions")}
                  <span className="text-xs text-muted-foreground ml-2">
                    ({totalFAQs})
                  </span>
                </button>

                {faqData.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => setSelectedCategory(category.id)}
                    className={cn(
                      "block w-full text-left px-3 py-2 rounded-lg transition-colors",
                      selectedCategory === category.id
                        ? "bg-chathero-mint/10 text-chathero-mint font-medium border-l-2 border-chathero-mint"
                        : "text-muted-foreground hover:text-foreground hover:bg-secondary/50"
                    )}
                  >
                    {category.title}
                    <span className="text-xs text-muted-foreground ml-2">
                      ({category.items.length})
                    </span>
                  </button>
                ))}
              </nav>
            </div>
          </aside>

          {/* FAQ List */}
          <div className="flex-1 space-y-12">
            {displayedCategories.map((category) => (
              <div key={category.id} id={category.id}>
                <h2 className="text-2xl font-bold text-primary mb-6 pb-2 border-b border-border">
                  {category.title}
                </h2>

                <Accordion type="single" collapsible className="space-y-3">
                  {category.items.map((item, index) => (
                    <AccordionItem
                      key={`${category.id}-${index}`}
                      value={`${category.id}-${index}`}
                      className="border border-border/50 rounded-xl px-6 data-[state=open]:bg-secondary/30 data-[state=open]:border-chathero-mint/30"
                    >
                      <AccordionTrigger className="text-left font-medium text-foreground hover:text-chathero-mint py-4 hover:no-underline">
                        {item.question}
                      </AccordionTrigger>
                      <AccordionContent className="text-muted-foreground pb-4 leading-relaxed">
                        {item.answer}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQContent;
